<template>
  <div class="chat-session-list">
    <!-- 头部 -->
    <div class="session-header">
      <h3>会话列表</h3>
      <div class="header-actions">
        <Select v-model="chatTypeFilter" placeholder="会话类型" clearable @on-change="handleFilterChange" style="width: 120px">
          <Option :value="1">职位沟通</Option>
          <Option :value="2">简历沟通</Option>
        </Select>
      </div>
    </div>

    <!-- 会话列表 -->
    <div class="session-list-container" v-loading="loading">
      <div
        v-for="session in sessions"
        :key="session.session_id"
        class="session-item"
        :class="{ 'active': currentSessionId === session.session_id }"
        @click="handleSelectSession(session)"
      >
        <!-- 头像 -->
        <div class="session-avatar">
          <img :src="session.target_user_avatar || defaultAvatar" :alt="session.target_user_name" />
          <Badge v-if="session.unread_count > 0" :count="session.unread_count" :offset="[-5, 5]" />
        </div>

        <!-- 会话信息 -->
        <div class="session-info">
          <div class="session-info-top">
            <span class="session-name">{{ session.target_user_name }}</span>
            <span class="session-time">{{ formatTime(session.last_message_time) }}</span>
          </div>
          <div class="session-info-bottom">
            <span class="session-type-tag">
              <Tag :color="session.chat_type === 1 ? 'blue' : 'green'" size="small">
                {{ session.chat_type === 1 ? '职位' : '简历' }}
              </Tag>
            </span>
            <span class="session-message" :class="{ 'unread': session.unread_count > 0 }">
              {{ formatLastMessage(session) }}
            </span>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="!loading && sessions.length === 0" class="empty-state">
        <Icon type="ios-chatbubbles-outline" size="60" color="#c5c8ce" />
        <p>暂无会话</p>
      </div>

      <!-- 加载更多 -->
      <div v-if="hasMore" class="load-more" @click="loadMore">
        <span v-if="!loadingMore">加载更多</span>
        <Spin v-else size="small"></Spin>
      </div>
    </div>
  </div>
</template>

<script>
import { getChatSessions } from '@/api/chat'
import moment from 'moment'

export default {
  name: 'ChatSessionList',
  props: {
    currentSessionId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      sessions: [],
      loading: false,
      loadingMore: false,
      chatTypeFilter: null,
      currentPage: 1,
      pageSize: 20,
      hasMore: true,
      defaultAvatar: 'https://file.iviewui.com/dist/a0e88e83800f138b94d2414621bd9704.png'
    }
  },
  mounted () {
    this.loadSessions()
  },
  methods: {
    /**
     * 加载会话列表
     */
    async loadSessions (append = false) {
      if (!append) {
        this.loading = true
        this.currentPage = 1
      } else {
        this.loadingMore = true
      }

      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize
        }

        if (this.chatTypeFilter) {
          params.chat_type = this.chatTypeFilter
        }

        const res = await getChatSessions(params)

        if (res.data.code === 0) {
          const newSessions = res.data.data.sessions || []

          if (append) {
            this.sessions = [...this.sessions, ...newSessions]
          } else {
            this.sessions = newSessions
          }

          // 判断是否还有更多数据
          this.hasMore = newSessions.length >= this.pageSize
        } else {
          this.$Message.error(res.data.msg || '加载会话列表失败')
        }
      } catch (error) {
        console.error('加载会话列表失败:', error)
        this.$Message.error('加载会话列表失败')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    /**
     * 加载更多会话
     */
    loadMore () {
      if (this.loadingMore || !this.hasMore) return
      this.currentPage++
      this.loadSessions(true)
    },

    /**
     * 选择会话
     */
    handleSelectSession (session) {
      this.$emit('select-session', session)
    },

    /**
     * 筛选条件变化
     */
    handleFilterChange () {
      this.loadSessions()
    },

    /**
     * 格式化时间
     */
    formatTime (timestamp) {
      if (!timestamp) return ''

      const now = moment()
      const messageTime = moment(timestamp * 1000)

      // 今天显示时间
      if (now.isSame(messageTime, 'day')) {
        return messageTime.format('HH:mm')
      }

      // 昨天
      if (now.subtract(1, 'days').isSame(messageTime, 'day')) {
        return '昨天'
      }

      // 一周内显示星期
      if (now.diff(messageTime, 'days') < 7) {
        const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
        return weekdays[messageTime.day()]
      }

      // 其他显示日期
      return messageTime.format('MM-DD')
    },

    /**
     * 格式化最后一条消息
     */
    formatLastMessage (session) {
      if (!session.last_message) return ''

      const msg = session.last_message

      // 系统消息
      if (msg.message_type === 3) {
        return '[系统消息]'
      }

      // 图片消息
      if (msg.message_type === 2) {
        return '[图片]'
      }

      // 文本消息，最多显示30个字符
      return msg.content.length > 30
        ? msg.content.substring(0, 30) + '...'
        : msg.content
    },

    /**
     * 更新会话列表中的某个会话
     */
    updateSession (sessionId, updates) {
      const index = this.sessions.findIndex(s => s.session_id === sessionId)
      if (index !== -1) {
        this.sessions.splice(index, 1, {
          ...this.sessions[index],
          ...updates
        })
      }
    },

    /**
     * 刷新会话列表
     */
    refresh () {
      this.loadSessions()
    }
  }
}
</script>

<style lang="less" scoped>
.chat-session-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e8eaec;

  .session-header {
    padding: 16px;
    border-bottom: 1px solid #e8eaec;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 500;
      color: #17233d;
    }
  }

  .session-list-container {
    flex: 1;
    overflow-y: auto;

    .session-item {
      display: flex;
      padding: 12px 16px;
      cursor: pointer;
      transition: background 0.2s;
      border-bottom: 1px solid #f8f8f9;

      &:hover {
        background: #f8f8f9;
      }

      &.active {
        background: #e8f4ff;
      }

      .session-avatar {
        position: relative;
        margin-right: 12px;
        flex-shrink: 0;

        img {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
        }
      }

      .session-info {
        flex: 1;
        min-width: 0;

        .session-info-top {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;

          .session-name {
            font-size: 14px;
            font-weight: 500;
            color: #17233d;
          }

          .session-time {
            font-size: 12px;
            color: #808695;
            flex-shrink: 0;
            margin-left: 8px;
          }
        }

        .session-info-bottom {
          display: flex;
          align-items: center;

          .session-type-tag {
            margin-right: 8px;
            flex-shrink: 0;
          }

          .session-message {
            font-size: 13px;
            color: #808695;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            &.unread {
              font-weight: 500;
              color: #515a6e;
            }
          }
        }
      }
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      color: #c5c8ce;

      p {
        margin-top: 16px;
        font-size: 14px;
      }
    }

    .load-more {
      padding: 16px;
      text-align: center;
      color: #2d8cf0;
      cursor: pointer;
      font-size: 14px;

      &:hover {
        color: #5cadff;
      }
    }
  }
}
</style>
